<template>
  <section>
    <page-sub-header :title="bankingInfo.sdl.label" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`sdl-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else>
      <sme-card v-if="timeGroupedLoansIssuedDataset.values.length" class="mb-4">
        <b-row class="align-items-center">
          <b-col lg="8" class="mb-3 mb-lg-0">
            <line-chart
              :labels="timeGroupDataset"
              :datasets="timeGroupedLoansIssuedDataset"
              :chart-options="{
                aspectRatio: getInitialAspectRatio(3, 2),
                scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedLoansIssuedDataset.name } } },
                ...createCountWithPercentageTooltip(timeGroupedLoansIssuedPercentageDataset),
              }"
              fill-gradient
            />
          </b-col>
          <b-col lg="4">
            <div class="stats-combined mb-3">
              <app-stat
                :stat="lastGroupedLoansIssued"
                :previous-stat="prevGroupedLoansIssued"
                :formatter="toCompactNumber"
                description="Loans"
                background-color="core-1-lighten-90"
                color="default"
              />
              <app-stat
                :stat="lastGroupedLoansIssuedPercentage"
                :previous-stat="prevGroupedLoansIssuedPercentage"
                :formatter="toNumber"
                suffix="%"
                description="Loans Percentage"
                background-color="core-1-lighten-90"
                color="default"
              />
            </div>
            <p class="mb-0">
              {{ timeGroupTitle }}, {{ toCompactNumber(lastGroupedLoansIssued) }} employees ({{
                lastGroupedLoansIssuedPercentage
              }}%) were issued a {{ bankingInfo.sdl.smallLabel }}.
              {{ loansIssuedPrevDiffText }}
            </p>
          </b-col>
        </b-row>
      </sme-card>

      <b-row class="mb-4">
        <b-col :cols="statCardCols" :lg="statCardCols" class="mb-4 mb-lg-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="avgLoanAmountDataset.calculated.mean || 0"
              :formatter="value => toCompactNumber(Math.round(value))"
              :prefix="currencySymbolFromCode(State.state.company.default_currency)"
              :description="`Average ${timeSeriesUnit}ly loan amount`"
              :class="[avgLoanAmountDataset.values.length && 'mb-3']"
              background-color="core-4-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="avgLoanAmountDataset.values.length"
              :labels="timeGroupDataset"
              :datasets="avgLoanAmountDataset"
              :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>

        <b-col :cols="statCardCols" :lg="statCardCols" class="mb-4 mb-lg-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="loanIssuedDataset.calculated.mean || 0"
              :formatter="value => toCompactNumber(Math.round(value))"
              :description="`Average ${timeSeriesUnit}ly loans issued`"
              :class="[loanIssuedDataset.values.length && 'mb-3']"
              background-color="core-5-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="loanIssuedDataset.values.length"
              :labels="timeGroupDataset"
              :datasets="loanIssuedDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>

        <b-col v-if="hasLoanPurposeData" cols="4" lg="4" class="mb-4 mb-lg-0">
          <sme-card class="h-100">
            <h6 class="mb-3 loan-purpose-title">Loan purpose</h6>
            <div class="d-flex justify-content-center">
              <div class="loan-purpose-chart">
                <donut-chart
                  :labels="loanPurposeLabels"
                  :values="loanPurposeValues"
                  :colors="loanPurposeColors"
                  :show-data-labels="false"
                  cutout="0%"
                  :chart-options="{ aspectRatio: 1, plugins: { tooltip: { enabled: true } } }"
                />
              </div>
            </div>
          </sme-card>
        </b-col>

        <b-col v-if="tescoPortalStubsEnabled" cols="3" lg="3" class="mb-4 mb-lg-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="loanApprovalRateDataset.calculated.mean || 0"
              :formatter="value => toNumber(Math.round(value))"
              suffix="%"
              :description="`Average ${timeSeriesUnit}ly loan approval rate`"
              :class="[loanApprovalRateDataset.values.length && 'mb-3']"
              background-color="core-3-lighten-80"
              color="default"
              small
            />
            <line-chart
              v-if="loanApprovalRateDataset.values.length"
              :labels="timeGroupDataset"
              :datasets="loanApprovalRateDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>

        <b-col v-if="tescoPortalStubsEnabled" cols="3" lg="3" class="mb-4 mb-lg-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="missedPaymentsDataset.calculated.mean || 0"
              :formatter="value => toNumber(Math.round(value * 100) / 100)"
              suffix="%"
              :description="`Average ${timeSeriesUnit}ly missed payments`"
              :class="[missedPaymentsDataset.values.length && 'mb-3']"
              background-color="core-6-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="missedPaymentsDataset.values.length"
              :labels="timeGroupDataset"
              :datasets="missedPaymentsDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>
      </b-row>

      <!-- Loan purpose standalone card for Tesco (4-col layout already full) -->
      <sme-card
        v-if="tescoPortalStubsEnabled && loanPurposeDatasets?.some(dataset => !!dataset.values.length)"
        class="mb-4"
      >
        <h6 class="mb-3 loan-purpose-title">Loan purpose</h6>
        <b-row class="align-items-center">
          <b-col md="6" class="d-flex justify-content-center mb-3 mb-md-0">
            <div class="loan-purpose-chart">
              <donut-chart
                :labels="loanPurposeLabels"
                :values="loanPurposeValues"
                :colors="loanPurposeColors"
                :show-data-labels="false"
                cutout="0%"
                :chart-options="{ aspectRatio: 1, plugins: { tooltip: { enabled: true } } }"
              />
            </div>
          </b-col>
          <b-col md="6">
            <div class="loan-purpose-legend">
              <div v-for="(label, index) in loanPurposeLabels" :key="label" class="loan-purpose-legend__item">
                <span class="loan-purpose-legend__dot" :style="{ backgroundColor: loanPurposeColors[index] }" />
                <span class="loan-purpose-legend__label">{{ label }}</span>
                <span class="loan-purpose-legend__value">{{ loanPurposeValues[index] }}%</span>
              </div>
            </div>
          </b-col>
        </b-row>
      </sme-card>
    </template>
  </section>
</template>

<script setup>
import merge from 'lodash/merge';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import DonutChart from '@/components/charts/DonutChart.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import {
  TIME_SERIES_VALUES,
  createCountWithPercentageTooltip,
  getDatasetColor,
  getInitialAspectRatio,
} from '@/utils/chart';
import { toCompactNumber, toNumber, currencySymbolFromCode } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { tescoPortalStubsEnabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();

const loading = ref(true);
const timeDataset = ref(null);
const loanIssuedDataset = ref(null);
const loansIssuedPercentageDataset = ref(null);
const avgLoanAmountDataset = ref(null);
const loanApprovalRateDataset = ref(null);
const missedPaymentsDataset = ref(null);

const loanPurposeDatasets = ref(null);

const { timeGroupDataset, timeGroupTitle, timeSeries, timeSeriesUnit, datasetToTimeGroupedDataset, getPrevDiffText } =
  useTimeGroupDataset(timeDataset);

const hasLoanPurposeData = computed(
  () => !tescoPortalStubsEnabled.value && loanPurposeDatasets.value?.some(dataset => !!dataset.values.length),
);
const statCardCols = computed(() => {
  if (tescoPortalStubsEnabled.value) {
    return 3;
  }
  return hasLoanPurposeData.value ? 4 : 6;
});

const loanPurposePieData = computed(() => {
  if (!loanPurposeDatasets.value) {
    return [];
  }
  return loanPurposeDatasets.value
    .map(dataset => ({
      label: dataset.name,
      value: Math.round((dataset.calculated?.mean || 0) * 1000) / 10,
      color: dataset.lineColor,
    }))
    .filter(item => item.value > 0);
});
const loanPurposeLabels = computed(() => loanPurposePieData.value.map(item => item.label));
const loanPurposeValues = computed(() => loanPurposePieData.value.map(item => item.value));
const loanPurposeColors = computed(() => loanPurposePieData.value.map(item => item.color));

const timeGroupedLoansIssuedDataset = computed(() => datasetToTimeGroupedDataset(loanIssuedDataset));
const timeGroupedLoansIssuedPercentageDataset = computed(() =>
  datasetToTimeGroupedDataset(loansIssuedPercentageDataset),
);
const lastGroupedLoansIssued = computed(() => timeGroupedLoansIssuedDataset.value?.calculated.last);
const prevGroupedLoansIssued = computed(() => timeGroupedLoansIssuedDataset.value?.calculated.prev);
const lastGroupedLoansIssuedPercentage = computed(() => timeGroupedLoansIssuedPercentageDataset.value?.calculated.last);
const prevGroupedLoansIssuedPercentage = computed(() => timeGroupedLoansIssuedPercentageDataset.value?.calculated.prev);
const loansIssuedPrevDiffText = computed(() => getPrevDiffText(timeGroupedLoansIssuedDataset));
const bankingInfo = computed(() => getBankingInfo());

watch([divisionId, timeSeries], () => getMetrics());

const getSdlMetrics = async () => {
  const { results } = await ApiClient.getSdlMetrics(timeSeries.value, divisionId.value);

  if (results?.length) {
    const time = results.find(result => result.id === 'time');
    const loansIssued = results.find(result => result.id === 'loans_issued');
    const enrolled = results.find(result => result.id === 'enrolled');
    const avgLoanAmount = results.find(result => result.id === 'avg_loan_amount');

    timeDataset.value = time;
    loanIssuedDataset.value = {
      ...loansIssued,
      name: 'Number of loans',
      lineColor: PaletteColors['core-5'],
    };
    loansIssuedPercentageDataset.value = {
      name: 'Percentage (%)',
      values: enrolled.values.map((value, index) =>
        value ? Number(toNumber((loansIssued.values[index] / value) * 100)) : 0,
      ),
    };
    avgLoanAmountDataset.value = {
      ...avgLoanAmount,
      name: 'Average amount',
      lineColor: PaletteColors['core-4'],
    };

    // Synthetic loan approval rate data (around 65%) - only for Tesco demo
    if (tescoPortalStubsEnabled.value) {
      loanApprovalRateDataset.value = {
        name: 'Approval rate (%)',
        lineColor: PaletteColors['core-3'],
        values: time.values.map(() => {
          // Generate random approval rate between 60-70%, centered around 65%
          const baseRate = 65;
          const variation = (Math.random() - 0.5) * 10; // ±5%
          return Math.max(55, Math.min(75, Math.round(baseRate + variation)));
        }),
        calculated: {
          mean: 65,
          last: 65,
          prev: 64,
        },
      };
    } else {
      loanApprovalRateDataset.value = { values: [], name: 'Approval rate (%)' };
    }

    // Synthetic missed payments data (between 0-0.8%) - only for Tesco demo
    if (tescoPortalStubsEnabled.value) {
      missedPaymentsDataset.value = {
        name: 'Missed payments (%)',
        lineColor: PaletteColors['core-6'],
        values: time.values.map(() => {
          // Generate random missed payments rate between 0-0.8%
          const maxRate = 0.8;
          const variation = Math.random() * maxRate;
          return Number(toNumber(variation));
        }),
        calculated: {
          mean: 0.4,
          last: 0.3,
          prev: 0.5,
        },
      };
    } else {
      missedPaymentsDataset.value = { values: [], name: 'Missed payments (%)' };
    }
  }
};

const getSdlPurposeMetrics = async () => {
  const { results } = await ApiClient.getSdlPurposeMetrics(timeSeries.value, divisionId.value);

  if (results?.length) {
    const categories = results.filter(result => result.id !== 'time');

    loanPurposeDatasets.value = categories.map((category, index) => ({
      ...category,
      lineColor: PaletteColors[getDatasetColor(index)],
      values: category.values.map(value => toNumber(value * 100)),
    }));
  }
};

const getMetrics = async () => {
  loading.value = true;

  await Promise.all([getSdlMetrics(), getSdlPurposeMetrics()]);

  loading.value = false;
};

onBeforeMount(() => getMetrics());

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values &&
    loanIssuedDataset.value?.values &&
    loansIssuedPercentageDataset.value?.values &&
    avgLoanAmountDataset.value?.values &&
    (!tescoPortalStubsEnabled.value || loanApprovalRateDataset.value?.values) &&
    (!tescoPortalStubsEnabled.value || missedPaymentsDataset.value?.values) &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => {
    const row = {
      time,
      loans: loanIssuedDataset.value.values[index],
      percentage: loansIssuedPercentageDataset.value.values[index],
      averageAmount: avgLoanAmountDataset.value.values[index],
    };
    if (tescoPortalStubsEnabled.value) {
      row.approvalRate = loanApprovalRateDataset.value.values[index];
      row.missedPayments = missedPaymentsDataset.value.values[index];
    }
    return row;
  });
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  const headers = [
    'Date',
    loanIssuedDataset.value.name,
    loansIssuedPercentageDataset.value.name,
    avgLoanAmountDataset.value.name,
  ];
  if (tescoPortalStubsEnabled.value) {
    headers.push(loanApprovalRateDataset.value.name);
    headers.push(missedPaymentsDataset.value.name);
  }
  return headers;
});

const exportFileRows = item => {
  const row = [item.time, item.loans, item.percentage, Math.round(item.averageAmount)];
  if (tescoPortalStubsEnabled.value) {
    row.push(item.approvalRate);
    row.push(Math.round(item.missedPayments * 100) / 100);
  }
  return row;
};
</script>

<style lang="scss" scoped>
.loan-purpose-title {
  font-weight: 700;
}

.loan-purpose-chart {
  width: 220px;
  max-width: 100%;
}

.loan-purpose-legend {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;

  &__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__label {
    flex: 1;
    font-size: 13px;
    color: var(--palette-color-default);
  }

  &__value {
    font-size: 13px;
    font-weight: 600;
    color: var(--palette-color-default);
  }
}

.stats-combined {
  display: flex;

  :deep(.app-stat) {
    flex: 1;
    height: auto; // Override height: 100% from AppStat to allow flexbox stretch
    border-radius: 0;
    box-shadow: none;

    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }

    .app-stat__value-container .app-stat__value {
      font-size: 28px;

      @media (min-width: 992px) {
        font-size: 32px;
      }
    }

    .app-stat__value-container {
      height: auto;
      min-height: 2rem;
    }
  }
}
</style>
